import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Upload, Check, Briefcase } from 'lucide-react';
import { cn } from '../lib/utils';
import { submitJobApplication } from '../lib/careers';
import { JobApplication } from '../types/jobs';

interface ApplicationFormProps {
  jobId: string;
  jobTitle: string;
}

const ApplicationForm = ({ jobId, jobTitle }: ApplicationFormProps) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    countryCode: '+971',
    resume: null as File | null,
    website: '' // honeypot field
  });
  const formStartTime = useRef<number>(Date.now());
  const [formErrors, setFormErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    resume: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });
  const [isDragging, setIsDragging] = useState(false);

  const validateForm = () => {
    const errors = {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      resume: ''
    };
    let isValid = true;

    // Honeypot check - if filled, it's a bot
    if (formData.website) {
      return false;
    }

    // Timing check - form filled too quickly (less than 3 seconds is suspicious)
    const timeTaken = (Date.now() - formStartTime.current) / 1000;
    if (timeTaken < 3) {
      return false;
    }

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
      isValid = false;
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
      isValid = false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!emailRegex.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
      isValid = false;
    }

    const phoneRegex = /^\d{7,15}$/;
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
      isValid = false;
    } else if (!phoneRegex.test(formData.phone)) {
      errors.phone = 'Please enter a valid phone number';
      isValid = false;
    }

    if (!formData.resume) {
      errors.resume = 'Resume is required';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      const timeTaken = Math.floor((Date.now() - formStartTime.current) / 1000);

      const applicationData: JobApplication = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        countryCode: formData.countryCode,
        resume: formData.resume,
        submissionTimeSeconds: timeTaken
      };

      await submitJobApplication(jobId, applicationData);
      
      setSubmitStatus({
        type: 'success',
        message: 'Application submitted successfully! We\'ll be in touch soon.'
      });
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        countryCode: '+971',
        resume: null,
        website: ''
      });
      formStartTime.current = Date.now();
      setFormErrors({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        resume: ''
      });
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: error instanceof Error ? error.message : 'Something went wrong. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateFile = (file: File): string | null => {
    // Check file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      return 'Please upload a PDF or Word document';
    }

    // Check file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      return 'File size must be less than 5MB';
    }

    return null;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const error = validateFile(file);
      if (error) {
        setFormErrors(prev => ({ ...prev, resume: error }));
        return;
      }

      setFormData(prev => ({ ...prev, resume: file }));
      setFormErrors(prev => ({ ...prev, resume: '' }));
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      const error = validateFile(file);
      if (error) {
        setFormErrors(prev => ({ ...prev, resume: error }));
        return;
      }

      setFormData(prev => ({ ...prev, resume: file }));
      setFormErrors(prev => ({ ...prev, resume: '' }));
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="bg-white p-8 rounded-xl shadow-lg border border-gray-200"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
          <Briefcase className="w-5 h-5 text-accent" />
        </div>
        <div>
          <h3 className="text-xl font-display font-semibold text-gray-900">
            Apply Now
          </h3>
          <p className="text-sm text-gray-600">{jobTitle}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {submitStatus.type && (
          <div className={cn(
            "p-4 rounded-lg flex items-center gap-2",
            submitStatus.type === 'success' ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
          )}>
            {submitStatus.type === 'success' && <Check className="w-5 h-5" />}
            <span className="text-sm">{submitStatus.message}</span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              First Name *
            </label>
            <input
              type="text"
              value={formData.firstName}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, firstName: e.target.value }));
                if (formErrors.firstName) setFormErrors(prev => ({ ...prev, firstName: '' }));
              }}
              className={cn(
                "w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-sm",
                formErrors.firstName ? "border-red-500" : "border-gray-300"
              )}
            />
            {formErrors.firstName && (
              <p className="mt-1 text-xs text-red-500">{formErrors.firstName}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Last Name *
            </label>
            <input
              type="text"
              value={formData.lastName}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, lastName: e.target.value }));
                if (formErrors.lastName) setFormErrors(prev => ({ ...prev, lastName: '' }));
              }}
              className={cn(
                "w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-sm",
                formErrors.lastName ? "border-red-500" : "border-gray-300"
              )}
            />
            {formErrors.lastName && (
              <p className="mt-1 text-xs text-red-500">{formErrors.lastName}</p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email *
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => {
              setFormData(prev => ({ ...prev, email: e.target.value }));
              if (formErrors.email) setFormErrors(prev => ({ ...prev, email: '' }));
            }}
            className={cn(
              "w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-sm",
              formErrors.email ? "border-red-500" : "border-gray-300"
            )}
          />
          {formErrors.email && (
            <p className="mt-1 text-xs text-red-500">{formErrors.email}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Phone Number *
          </label>
          <div className="flex gap-2">
            <select
              value={formData.countryCode}
              onChange={(e) => setFormData(prev => ({ ...prev, countryCode: e.target.value }))}
              className="px-2 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-sm"
            >
              <option value="+971">+971</option>
              <option value="+1">+1</option>
              <option value="+44">+44</option>
              <option value="+91">+91</option>
            </select>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                setFormData(prev => ({ ...prev, phone: value }));
                if (formErrors.phone) setFormErrors(prev => ({ ...prev, phone: '' }));
              }}
              className={cn(
                "flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-sm",
                formErrors.phone ? "border-red-500" : "border-gray-300"
              )}
              placeholder="501234567"
            />
          </div>
          {formErrors.phone && (
            <p className="mt-1 text-xs text-red-500">{formErrors.phone}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Resume *
          </label>
          <div
            className={cn(
              "border-2 border-dashed rounded-lg p-3 text-center transition-colors duration-200",
              formErrors.resume ? "border-red-300 bg-red-50" :
              isDragging ? "border-accent bg-accent/5" : "border-gray-300 hover:border-accent"
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleFileChange}
              className="hidden"
              id="resume-upload-form"
            />
            <label htmlFor="resume-upload-form" className="cursor-pointer">
              <Upload className="w-6 h-6 text-gray-400 mx-auto mb-1" />
              {formData.resume ? (
                <p className="text-xs text-gray-700">{formData.resume.name}</p>
              ) : (
                <>
                  <p className="text-xs text-gray-600">
                    {isDragging ? 'Drop file here' : 'Click to upload or drag and drop'}
                  </p>
                  <p className="text-xs text-gray-500">PDF, DOC, DOCX (max 5MB)</p>
                </>
              )}
            </label>
          </div>
          {formErrors.resume && (
            <p className="mt-1 text-xs text-red-500">{formErrors.resume}</p>
          )}
        </div>

        {/* Honeypot field - hidden from users, only bots will fill it */}
        <div className="hidden" aria-hidden="true">
          <label htmlFor="website">Website</label>
          <input
            type="text"
            id="website"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={formData.website}
            onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
          />
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className={cn(
            "w-full px-4 py-3 bg-accent text-white rounded-lg font-medium",
            "hover:bg-accent/90 transition-colors duration-200",
            "disabled:opacity-50 disabled:cursor-not-allowed"
          )}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Application'}
        </button>
      </form>
    </motion.div>
  );
};

export default ApplicationForm;